import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Module-level map: pathname+search -> scrollY (survives component remounts)
const scrollPositions = new Map();

export default function ScrollManager() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType(); // "PUSH" | "POP" | "REPLACE"
  const currentKeyRef = useRef(pathname + search);
  const restoreTokenRef = useRef(0);

  // Save scroll position synchronously on every scroll event for the CURRENT key.
  // Guard: only save when window.location still matches currentKeyRef — this prevents
  // the auto-clamped scroll events that fire during the React unmount/remount of the
  // outgoing page from overwriting the saved position with a junk value (e.g. 281).
  useEffect(() => {
    const onScroll = () => {
      const liveKey = window.location.pathname + window.location.search;
      if (liveKey !== currentKeyRef.current) return; // route has changed; ignore
      scrollPositions.set(currentKeyRef.current, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (hash) return; // anchor links — leave alone
    const newKey = pathname + search;
    const token = ++restoreTokenRef.current;

    if (navType === "POP") {
      const target = scrollPositions.get(newKey) ?? 0;
      // Update currentKeyRef BEFORE any scrollTo so the scroll handler doesn't
      // overwrite the previous page's saved position with the new page's scrollY.
      currentKeyRef.current = newKey;
      if (target === 0) {
        window.scrollTo(0, 0);
        return;
      }
      // Strategy:
      // 1. Try to land at target as early as possible (synchronously on first frame).
      // 2. Poll documentHeight via rAF for up to 1.5s for late-mounted content.
      // 3. **Crucially: stop the loop the instant the user interacts (touchstart,
      //    wheel, keydown).** This prevents the 1–2s "stutter" where the
      //    correction loop fought with the user's first scroll attempt.
      window.scrollTo(0, target); // first attempt immediately

      const start = performance.now();
      const GROW_TIMEOUT_MS = 1500;
      const SETTLE_MS = 250;
      let reachedAt = 0;
      let aborted = false;

      const abort = () => { aborted = true; };
      const ABORT_EVENTS = ["wheel", "touchstart", "keydown", "mousedown", "pointerdown"];
      ABORT_EVENTS.forEach((ev) =>
        window.addEventListener(ev, abort, { passive: true, once: true })
      );
      const cleanup = () => {
        ABORT_EVENTS.forEach((ev) => window.removeEventListener(ev, abort));
      };

      const getMax = () =>
        Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight,
        ) - window.innerHeight;

      const tick = () => {
        if (aborted || token !== restoreTokenRef.current) { cleanup(); return; }
        const max = getMax();
        const now = performance.now();

        if (max >= target - 4) {
          if (Math.abs(window.scrollY - target) > 2) {
            window.scrollTo(0, target);
          }
          if (!reachedAt) reachedAt = now;
          if (now - reachedAt < SETTLE_MS) {
            requestAnimationFrame(tick);
          } else {
            cleanup();
          }
          return;
        }
        if (now - start > GROW_TIMEOUT_MS) {
          window.scrollTo(0, Math.min(target, Math.max(max, 0)));
          cleanup();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } else {
      // PUSH or REPLACE → fresh page, scroll to top
      currentKeyRef.current = newKey;
      window.scrollTo(0, 0);
    }
  }, [pathname, search, hash, navType]);

  return null;
}
